/**
 * Set styles by creating or updating a <style> tag with unique ID and appending it to the document's head if doesn't exist
 * Import function into your component such as "import { moveInlineStylesToHead } from etc '../_helpers/content-security-policy';"
 * Add an ID to your component wrapper such as id="your-component-tag-name"
 * Call a function "moveInlineStylesToHead(tagName)" and pass an arg but before that be sure that arg collect right ID "const tagName = this.el.id; // Get the component's ID"
 * Be sure where you need to call function depends on component functionality "componentDidUpdate() or componentDidLoad()" or hook which you need to handle it right
 * At the end add same ID into a component.spec.ts file how tests can pass correctly
 *
 * @param {string} tagName The ID to identify the style tag and also the ID head style tag to be updated with csp_ prefix such as "csp_ds-your-component-name"
 */

export const moveInlineStylesToHead = (tagName: string) => {
	if (tagName) {
		const elements = document.querySelectorAll(`#${tagName} *`);
		let headStyleTag = document.head.querySelector(`#csp_${tagName}`);
		let headStyleText = headStyleTag?.textContent || '';

		if (!headStyleTag) {
			// If the style tag doesn't exist, create a new one
			headStyleTag = document.createElement('style');
			headStyleTag.id = `csp_${tagName}`;

			//setting nonce for newly created style tag
			let nonce = getScriptNonce();
			headStyleTag.setAttribute('nonce', nonce);

			document.head.appendChild(headStyleTag);
		}

		elements.forEach((element) => {
			if (element.hasAttribute('style')) {
				const getInlineStyles = element.getAttribute('style');
				const classes = element.classList
					.toString()
					.split(' ')
					.filter((className) => className.trim() !== '')
					.map((className) => `.${className}`)
					.join('');

				element.removeAttribute('style');

				const selector = `#${tagName} \n ${classes}`;

				if (!headStyleText.includes(selector)) {
					// If the selector is not found, add it with the inline styles.
					headStyleText += `${selector} { ${getInlineStyles} }\n`;
				} else {
					// If the selector already exists, merge the inline styles with existing styles.
					const existingStyle = getExistingStyleForSelector(
						selector,
						headStyleText
					);

					// Convert existing and new styles to objects for comparison and merging.
					const existingStyleObject = parseStyleToObject(existingStyle);
					const newStyleObject = parseStyleToObject(getInlineStyles);

					// Iterate through properties and update existing styles with new values.
					compareAndUpdateStyles(existingStyleObject, newStyleObject);
					// Reconstruct the updated style object to string
					const updatedStyle = getStyleStringFromObject(existingStyleObject);

					// Replace the old style with the updated style in the <style> tag's content.
					headStyleText = headStyleText.replace(
						`${selector} {${existingStyle}}`,
						`${selector} { ${updatedStyle}}`
					);
				}
			}
		});
		headStyleTag.textContent = headStyleText;
	}
};

// Function to parse a CSS style string into an object
function parseStyleToObject(styleString: string) {
	let styleObject = {};
	let properties = styleString.split(';');
	properties.forEach((p) => {
		let property = p.trim();
		if (property) {
			const [propertyName, propertyValue] = property
				.split(':')
				.map((str) => str.trim());
			styleObject[propertyName] = propertyValue;
		}
	});
	return styleObject;
}

function getExistingStyleForSelector(selector: string, from: string) {
	const existingSelectorIndex = from.indexOf(selector);
	const startOfSelectorIndex = from.indexOf('{', existingSelectorIndex);
	const endOfSelectorIndex = from.indexOf('}', existingSelectorIndex);
	const existingStyle = from.substring(
		startOfSelectorIndex + 1,
		endOfSelectorIndex
	);
	return existingStyle;
}

function compareAndUpdateStyles(existingStyleObject, newStyleObject) {
	for (const property in newStyleObject) {
		if (existingStyleObject[property] !== newStyleObject[property]) {
			existingStyleObject[property] = newStyleObject[property];
		}
	}
}

function getStyleStringFromObject(existingStyleObject) {
	let updatedStyle = '';
	for (const prop in existingStyleObject) {
		updatedStyle += `${prop}: ${existingStyleObject[prop]}; `;
	}
	return updatedStyle;
}

/* Returns the nonce. */
function getScriptNonce() {
	let script =
		document.querySelector && document.querySelector('script[nonce]');
	if (script) {
		let nonce = script['nonce'] || script.getAttribute('nonce');
		return nonce;
	}
	return '';
}

// this function should be used to clean up the CSP styles from headers when component is disconnected.
// disconnectedCallback() {
// 	removeInlineStylesFromHead(this.el.id);
// }
export const removeInlineStylesFromHead = (tagName: string) => {
	document.head.querySelector(`#csp_${tagName}`)?.remove();
};

export const exportedForTesting = {
	parseStyleToObject,
	getExistingStyleForSelector,
	compareAndUpdateStyles,
	getStyleStringFromObject,
};
